import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, ArrowLeft, MessageCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import { JsonLd, articleSchema, breadcrumbSchema, faqSchema } from '../../components/JsonLd';
import { getPostBySlug, getRelatedPosts, postAlternates } from '../../data/blog/posts';
import { localizedPath } from '../../config/langs';
import { NAP } from '../../config/nap';
import { ui } from '../../config/seoCopy';

export default function BlogPostPage() {
    const { slug } = useParams();
    const { i18n } = useTranslation();
    const lang = (i18n.language || 'en').split('-')[0];

    const post = getPostBySlug(slug);
    // A post lives only under its own language's prefix. Reached under
    // another one (an old link, a hand-typed URL), go to the real address —
    // a full load, because the language prefix is the router's basename.
    const misplaced = post && post.lang !== lang;
    useEffect(() => {
        if (misplaced) window.location.replace(localizedPath(`/blog/${post.slug}`, post.lang));
    }, [misplaced, post]);

    if (!post) {
        return <Navigate to="/blog" replace />;
    }
    if (misplaced) return null;

    const isRtl = post.lang === 'ar';
    const related = getRelatedPosts(post.slug, post.lang, 3);

    const labels = {
        ar: { back: 'العودة للمدوّنة', minRead: 'دقيقة قراءة', related: 'مقالات ذات صلة', cta: 'تحدّث مع مستشار عقاري', faqHeading: 'أسئلة شائعة' },
        en: { back: 'Back to Blog', minRead: 'min read', related: 'Related articles', cta: 'Talk to a real estate advisor', faqHeading: 'Frequently Asked Questions' },
        ru: { back: 'Назад в блог', minRead: 'мин чтения', related: 'Похожие статьи', cta: 'Связаться с консультантом', faqHeading: 'Часто задаваемые вопросы' },
        zh: { back: '返回博客', minRead: '分钟阅读', related: '相关文章', cta: '联系顾问', faqHeading: '常见问题' }
    };
    const L = labels[post.lang] || labels.en;

    const formatDate = (iso) => {
        const d = new Date(iso);
        try {
            return d.toLocaleDateString(
                post.lang === 'ar' ? 'ar-AE' : post.lang === 'ru' ? 'ru-RU' : post.lang === 'zh' ? 'zh-CN' : 'en-GB',
                { year: 'numeric', month: 'long', day: 'numeric' }
            );
        } catch {
            return d.toLocaleDateString();
        }
    };

    return (
        <>
            <SEO
                fullTitle={post.title}
                description={post.description}
                path={`/blog/${post.slug}`}
                alternates={postAlternates(post)}
                keywords={post.keywords}
                type="article"
                publishedAt={post.publishedAt}
                updatedAt={post.updatedAt}
                author="Al Zaeem Al Ainawi Real Estate"
            />
            <JsonLd data={articleSchema({
                title: post.title,
                description: post.description,
                image: post.image,
                slug: post.slug,
                publishedAt: post.publishedAt,
                updatedAt: post.updatedAt,
                lang: post.lang
            })} />
            <JsonLd data={breadcrumbSchema([
                { name: ui(post.lang).home, url: '/' },
                { name: ui(post.lang).blog, url: '/blog' },
                { name: post.title, url: `/blog/${post.slug}` }
            ])} />
            {post.faq && post.faq.length > 0 && <JsonLd data={faqSchema(post.faq, { url: `${NAP.url}${localizedPath(`/blog/${post.slug}`, post.lang)}`, lang: post.lang })} />}

            <article style={{
                minHeight: '100vh',
                background: 'var(--color-dark-bg, #0a0a0f)',
                padding: '60px 20px 80px',
                fontFamily: "'Inter', sans-serif",
                direction: isRtl ? 'rtl' : 'ltr'
            }}>
                <div style={{ maxWidth: '760px', margin: '0 auto' }}>

                    {/* Back link */}
                    <Link to="/blog" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        color: 'rgba(255,255,255,0.5)', textDecoration: 'none',
                        fontSize: '14px', marginBottom: '32px', fontWeight: 500
                    }}>
                        <ArrowLeft size={16} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />
                        {L.back}
                    </Link>

                    {/* Meta */}
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '16px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <Calendar size={14} /> {formatDate(post.publishedAt)}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={14} /> {post.readingTime} {L.minRead}
                        </span>
                    </div>

                    {/* Title */}
                    <Breadcrumbs items={[{ name: ui(post.lang).home, to: '/' }, { name: ui(post.lang).blog, to: '/blog' }, { name: post.title }]} />
                    <h1 style={{
                        fontSize: 'clamp(30px, 4.2vw, 46px)',
                        color: '#fff',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        margin: '0 0 32px'
                    }}>
                        {post.title}
                    </h1>

                    {/* Content */}
                    <div
                        className="blog-content"
                        style={{ color: 'rgba(255,255,255,0.85)', fontSize: '17px', lineHeight: 1.75 }}
                        dangerouslySetInnerHTML={{ __html: post.content }}
                    />

                    {/* FAQ */}
                    {post.faq && post.faq.length > 0 && (
                        <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <h2 style={{ color: '#fff', fontSize: '28px', fontWeight: 700, marginBottom: '24px' }}>
                                {L.faqHeading}
                            </h2>
                            <div style={{ display: 'grid', gap: '14px' }}>
                                {post.faq.map((it, idx) => (
                                    <details key={idx} style={{
                                        background: 'rgba(255,255,255,0.03)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        borderRadius: '10px',
                                        padding: '18px 22px'
                                    }}>
                                        <summary style={{
                                            color: '#fff', fontWeight: 600, fontSize: '16px',
                                            cursor: 'pointer', listStyle: 'none',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px'
                                        }}>
                                            {it.q}
                                            <span style={{ color: 'var(--color-gold, #d4af37)', fontSize: '20px' }}>+</span>
                                        </summary>
                                        <p style={{
                                            marginTop: '12px',
                                            color: 'rgba(255,255,255,0.7)',
                                            fontSize: '15px', lineHeight: 1.7
                                        }}>
                                            {it.a}
                                        </p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* CTA */}
                    <div style={{
                        marginTop: '48px', padding: '28px 32px',
                        background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(107,76,147,0.08))',
                        border: '1px solid rgba(212,175,55,0.25)',
                        borderRadius: '14px',
                        display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap'
                    }}>
                        <MessageCircle size={40} color="var(--color-gold, #d4af37)" />
                        <div style={{ flex: 1, minWidth: '220px' }}>
                            <p style={{ color: '#fff', fontSize: '17px', fontWeight: 600, margin: '0 0 6px' }}>
                                {post.lang === 'ar' ? 'استشارة عقاريّة مجّانيّة' :
                                    post.lang === 'ru' ? 'Бесплатная консультация' :
                                        post.lang === 'zh' ? '免费房地产咨询' :
                                            'Free real estate consultation'}
                            </p>
                            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', margin: 0 }}>
                                {post.lang === 'ar' ? 'أرسل رسالة على واتساب — نردّ في دقائق.' :
                                    post.lang === 'ru' ? 'Отправьте сообщение в WhatsApp — ответим за минуты.' :
                                        post.lang === 'zh' ? '发送 WhatsApp 消息 — 我们几分钟内回复。' :
                                            'Message us on WhatsApp — we reply in minutes.'}
                            </p>
                        </div>
                        <a href="https://wa.me/971521138999" target="_blank" rel="noreferrer" style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'var(--color-gold, #d4af37)', color: '#000',
                            padding: '12px 22px', borderRadius: '10px',
                            fontWeight: 700, fontSize: '15px', textDecoration: 'none'
                        }}>
                            {L.cta} <ArrowLeft size={16} style={{ transform: isRtl ? 'none' : 'scaleX(-1)' }} />
                        </a>
                    </div>

                    {/* Related */}
                    {related.length > 0 && (
                        <div style={{ marginTop: '60px', paddingTop: '32px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <h2 style={{ color: '#fff', fontSize: '22px', fontWeight: 700, marginBottom: '20px' }}>
                                {L.related}
                            </h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                                {related.map(r => (
                                    <Link key={r.slug} to={`/blog/${r.slug}`} style={{
                                        padding: '16px 20px',
                                        background: 'rgba(255,255,255,0.03)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        borderRadius: '10px',
                                        textDecoration: 'none',
                                        display: 'block'
                                    }}>
                                        <div style={{ color: '#fff', fontSize: '15px', fontWeight: 600, lineHeight: 1.4, marginBottom: '6px' }}>
                                            {r.title}
                                        </div>
                                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                                            {r.readingTime} {L.minRead}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </article>

            {/* Content styles (h2, h3, p, table, ul) */}
            <style>{`
              .blog-content h2 { color: #fff; font-size: 26px; font-weight: 700; margin: 40px 0 16px; letter-spacing: -0.01em; }
              .blog-content h3 { color: #fff; font-size: 20px; font-weight: 600; margin: 28px 0 12px; }
              .blog-content p { margin: 0 0 16px; color: rgba(255,255,255,0.82); }
              .blog-content p.lead { font-size: 19px; color: rgba(255,255,255,0.9); line-height: 1.65; margin-bottom: 28px; }
              .blog-content ul, .blog-content ol { padding-${isRtl ? 'right' : 'left'}: 22px; margin: 0 0 20px; color: rgba(255,255,255,0.82); }
              .blog-content li { padding: 4px 0; }
              .blog-content strong { color: #fff; font-weight: 700; }
              .blog-content em { color: var(--color-gold, #d4af37); font-style: normal; }
              .blog-content table { width: 100%; border-collapse: collapse; margin: 20px 0 28px; font-size: 14px; background: rgba(255,255,255,0.02); border-radius: 8px; overflow: hidden; }
              .blog-content thead { background: rgba(212,175,55,0.08); }
              .blog-content th { padding: 10px 14px; text-align: ${isRtl ? 'right' : 'left'}; color: var(--color-gold, #d4af37); font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid rgba(255,255,255,0.08); }
              .blog-content td { padding: 10px 14px; color: rgba(255,255,255,0.8); border-bottom: 1px solid rgba(255,255,255,0.04); }
              .blog-content tr:last-child td { border-bottom: 0; }
            `}</style>
        </>
    );
}
